/**
 * Customer-360 screen — the header + a six-tab body (Overview / Subscription / Usage / Users /
 * Reports / Invoices). The active tab is MIRRORED into ?tab= so a tab is shareable, survives reload,
 * and the search drill-down (?tab=users / ?tab=invoice) lands on the right tab. Each tab loads its own
 * data lazily (only when opened) so a customer open is cheap.
 *
 * The Invoices tab is MONEY-ONLY: it's absent from the tab strip entirely when !sees_money, and a
 * deep-link to ?tab=invoice for a non-money role falls back to Overview rather than rendering a locked
 * tab. The lock icon on the (visible) Invoice tab signals its money nature for the roles that have it.
 */

import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useParams, useSearchParams } from "react-router-dom";
import { Lock } from "lucide-react";
import { Tabs, TabPanel, ErrorState, type TabItem } from "@/components/ui";
import { PermissionGate } from "@/components/portal/PermissionGate";
import { useRbac } from "@/context/RbacContext";
import { CustomerHeader } from "@/features/customer/CustomerHeader";
import { OverviewTab } from "@/features/customer/tabs/OverviewTab";
import { SubscriptionTab } from "@/features/customer/tabs/SubscriptionTab";
import { UsageTab } from "@/features/customer/tabs/UsageTab";
import { UsersTab } from "@/features/customer/tabs/UsersTab";
import { ReportsTab } from "@/features/customer/tabs/ReportsTab";
import { InvoicesTab } from "@/features/customer/tabs/InvoicesTab";

const TAB_IDS = ["overview", "subscription", "usage", "users", "reports", "invoice"] as const;
type TabId = (typeof TAB_IDS)[number];

export function CustomerPage() {
  const { t } = useTranslation();
  // Page-level gate (consistent with DashboardPage): a role lacking customer:read gets a clear
  // forbidden state, not an infinite skeleton (the data hooks would stay disabled and never resolve).
  return (
    <PermissionGate
      permission="customer:read"
      fallback={<ErrorState title={t("state.forbiddenTitle")} body={t("state.forbiddenBody")} />}
    >
      <CustomerBody />
    </PermissionGate>
  );
}

function CustomerBody() {
  const { t } = useTranslation();
  const { id = "" } = useParams();
  const { canSeeMoney } = useRbac();
  const [params, setParams] = useSearchParams();

  // The Invoice tab only exists for a money role.
  const seesMoney = canSeeMoney();

  // Resolve the active tab from ?tab=, defaulting (and falling back) to overview. A non-money role that
  // deep-links to ?tab=invoice is sent to overview rather than shown a tab it can't have.
  const requested = (params.get("tab") ?? "overview") as TabId;
  const active: TabId =
    TAB_IDS.includes(requested) && (requested !== "invoice" || seesMoney) ? requested : "overview";

  // Normalise the URL if it asked for an impossible tab (e.g. ?tab=invoice as a Viewer).
  useEffect(() => {
    if (requested !== active) {
      setParams(
        (prev) => {
          const p = new URLSearchParams(prev);
          if (active === "overview") p.delete("tab");
          else p.set("tab", active);
          return p;
        },
        { replace: true },
      );
    }
  }, [requested, active, setParams]);

  function selectTab(value: string) {
    setParams(
      (prev) => {
        const p = new URLSearchParams(prev);
        if (value === "overview") p.delete("tab");
        else p.set("tab", value);
        // Leaving the Users tab clears the member deep-link highlight.
        if (value !== "users") p.delete("member");
        return p;
      },
      { replace: false },
    );
  }

  const items: TabItem[] = [
    { value: "overview", label: t("customer.tabs.overview") },
    { value: "subscription", label: t("customer.tabs.subscription") },
    { value: "usage", label: t("customer.tabs.usage") },
    { value: "users", label: t("customer.tabs.users") },
    { value: "reports", label: t("customer.tabs.reports") },
    // Invoice tab — money only. Hidden entirely otherwise.
    ...(seesMoney
      ? [
          {
            value: "invoice",
            label: t("customer.tabs.invoice"),
            adornment: <Lock className="size-3 text-(--text-subtle)" aria-hidden />,
          } satisfies TabItem,
        ]
      : []),
  ];

  const panelBase = "customer-tab";

  return (
    <section aria-labelledby="customer-title" className="flex flex-col gap-6">
      <span id="customer-title" className="sr-only">
        {t("customer.title")}
      </span>
      <CustomerHeader accountId={id} />

      <Tabs items={items} value={active} onValueChange={selectTab} label={t("customer.title")} />

      <TabPanel id={panelBase} value="overview" active={active === "overview"}>
        <OverviewTab accountId={id} />
      </TabPanel>
      <TabPanel id={panelBase} value="subscription" active={active === "subscription"}>
        <SubscriptionTab accountId={id} />
      </TabPanel>
      <TabPanel id={panelBase} value="usage" active={active === "usage"}>
        <UsageTab accountId={id} />
      </TabPanel>
      <TabPanel id={panelBase} value="users" active={active === "users"}>
        <UsersTab accountId={id} />
      </TabPanel>
      <TabPanel id={panelBase} value="reports" active={active === "reports"}>
        <ReportsTab accountId={id} />
      </TabPanel>
      {seesMoney && (
        <TabPanel id={panelBase} value="invoice" active={active === "invoice"}>
          <InvoicesTab accountId={id} />
        </TabPanel>
      )}
    </section>
  );
}
